import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';
import OnlineMembersPanel from '../../components/common/OnlineMembersPanel';
import ChatPanel from '../../components/common/ChatPanel';

const SOURCE_BADGES = {
  contact: { label: 'Contact', cls: 'bg-blue-100 text-blue-700' },
  surveyor_inquiry: { label: 'Surveyor', cls: 'bg-purple-100 text-purple-700' },
  member: { label: 'Member', cls: 'bg-[#0B5D1E]/10 text-[#0B5D1E]' },
  admin: { label: 'Admin', cls: 'bg-[#C8A951]/20 text-[#8B6914]' },
};

export default function AdminMessages() {
  const { canDelete } = usePermissions();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [readFilter, setReadFilter] = useState('');
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  // Compose state
  const [showCompose, setShowCompose] = useState(false);
  const [composeData, setComposeData] = useState({ subject: '', message: '' });
  const [composeAttachment, setComposeAttachment] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [searchingMembers, setSearchingMembers] = useState(false);
  const [sending, setSending] = useState(false);
  const [showOnlinePanel, setShowOnlinePanel] = useState(false);
  const [chatUser, setChatUser] = useState(null);
  const composeFileRef = React.useRef(null);
  const composeFormRef = React.useRef(null);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (sourceFilter) params.append('source', sourceFilter);
      if (readFilter) params.append('is_read', readFilter);
      const res = await api.get(`/admin/messages/?${params}`);
      setMessages(res.data.results || res.data);
    } catch (err) {
      toast.error('Failed to load messages.');
    } finally {
      setLoading(false);
    }
  }, [search, sourceFilter, readFilter]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  useEffect(() => {
    if (showCompose && composeFormRef.current) {
      composeFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showCompose]);

  const markRead = async (msg) => {
    try {
      await api.patch(`/admin/messages/${msg.id}/`, { is_read: true });
      setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, is_read: true } : m));
      if (selectedMsg?.id === msg.id) setSelectedMsg({ ...selectedMsg, is_read: true });
    } catch {
      toast.error('Failed to update');
    }
  };

  const deleteMsg = async (msg) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/admin/messages/${msg.id}/`);
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
      if (selectedMsg?.id === msg.id) setSelectedMsg(null);
      toast.success('Message deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) { toast.error('Reply cannot be empty.'); return; }
    setReplying(true);
    try {
      const res = await api.post(`/admin/messages/${selectedMsg.id}/reply/`, { admin_reply: replyText });
      const updated = res.data;
      setMessages((prev) => prev.map((m) => m.id === updated.id ? updated : m));
      setSelectedMsg(updated);
      setReplyText('');
      toast.success('Reply sent!');
    } catch {
      toast.error('Failed to send reply.');
    } finally {
      setReplying(false);
    }
  };

  // ── Compose (admin → member) ──
  useEffect(() => {
    if (!memberSearch.trim()) { setMemberResults([]); return; }
    const timer = setTimeout(async () => {
      setSearchingMembers(true);
      try {
        const res = await api.get(`/accounts/admin/members/?search=${encodeURIComponent(memberSearch)}`);
        setMemberResults((res.data.results || res.data).slice(0, 8));
      } catch { /* ignore */ }
      finally { setSearchingMembers(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [memberSearch]);

  const handleSendToMember = async (e) => {
    e.preventDefault();
    if (!selectedRecipient) { toast.error('Select a member.'); return; }
    if (!composeData.subject.trim() || !composeData.message.trim()) { toast.error('Subject and message are required.'); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('recipient_id', selectedRecipient.id);
      fd.append('subject', composeData.subject);
      fd.append('message', composeData.message);
      if (composeAttachment) fd.append('attachment', composeAttachment);
      await api.post('/admin/messages/send/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Message sent to member!');
      setShowCompose(false);
      setComposeData({ subject: '', message: '' });
      setComposeAttachment(null);
      setSelectedRecipient(null);
      setMemberSearch('');
      fetchMessages();
    } catch {
      toast.error('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const unreadCount = messages.filter((m) => !m.is_read).length;
  const badge = (src) => SOURCE_BADGES[src] || SOURCE_BADGES.contact;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
      <div className="flex gap-6">
        {/* Main Messages Area */}
        <div className="flex-1 min-w-0">

      {/* Chat Panel - shown when chatting with a member */}
      {chatUser ? (
        <div className="flex flex-col" style={{ height: 'calc(100vh - 7rem)' }}>
          <div className="flex items-center gap-3 mb-3 flex-shrink-0">
            <button onClick={() => setChatUser(null)}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: 'Inter, sans-serif' }}>Messages</h1>
              <p className="text-gray-500 text-sm mt-0.5">Chatting with {chatUser.name}</p>
            </div>
          </div>
          <div className="flex-1 min-h-0">
            <ChatPanel chatUser={chatUser} onClose={() => setChatUser(null)} currentUserRole="admin" />
          </div>
        </div>
      ) : (
      <>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: 'Inter, sans-serif' }}>Messages</h1>
          <p className="text-gray-500 text-sm mt-1">
            All messages from Contact, Surveyor Inquiries &amp; Members
            {unreadCount > 0 && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">{unreadCount} unread</span>}
          </p>
        </div>
        <button onClick={() => { setShowCompose(!showCompose); setSelectedMsg(null); }}
          className="bg-[#0B5D1E] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition flex items-center gap-2">
          {showCompose ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              Cancel
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
              Message Member
            </>
          )}
        </button>
      </div>

      {/* Compose Form */}
      {showCompose && (
        <form ref={composeFormRef} onSubmit={handleSendToMember} className="bg-white rounded-xl shadow-sm border mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-[#062E10] to-[#0B5D1E] px-6 py-4">
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              Send Message to Member
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {/* Member search */}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Recipient</label>
              {selectedRecipient ? (
                <div className="flex items-center gap-3 bg-[#0B5D1E]/5 border border-[#0B5D1E]/20 rounded-lg px-4 py-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#0B5D1E] text-white flex items-center justify-center text-sm font-bold">
                    {(selectedRecipient.other_names || selectedRecipient.surname || '?')[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {selectedRecipient.other_names} {selectedRecipient.surname}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{selectedRecipient.email}{selectedRecipient.membership_number ? ` · ${selectedRecipient.membership_number}` : ''}</p>
                  </div>
                  <button type="button" onClick={() => { setSelectedRecipient(null); setMemberSearch(''); }}
                    className="text-red-400 hover:text-red-600 p-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input type="text" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="Search by name, email, or membership number..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                  {searchingMembers && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#0B5D1E]/30 border-t-[#0B5D1E] rounded-full animate-spin" />}
                  {memberResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                      {memberResults.map((m) => (
                        <button key={m.id} type="button" onClick={() => { setSelectedRecipient(m); setMemberSearch(''); setMemberResults([]); }}
                          className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50 last:border-0">
                          <div className="w-7 h-7 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-xs font-bold">
                            {(m.other_names || m.surname || '?')[0]}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{m.other_names} {m.surname}</p>
                            <p className="text-xs text-gray-400 truncate">{m.email}{m.membership_number ? ` · ${m.membership_number}` : ''}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Subject</label>
              <input type="text" required value={composeData.subject}
                onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                placeholder="Enter message subject..."
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Message</label>
              <textarea required rows={4} value={composeData.message}
                onChange={(e) => setComposeData({ ...composeData, message: e.target.value })}
                placeholder="Write your message..."
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none resize-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Attachment (optional)</label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#0B5D1E] hover:bg-[#0B5D1E]/5 transition text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                  {composeAttachment ? 'Change file' : 'Attach file'}
                  <input ref={composeFileRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    onChange={(e) => { const f = e.target.files[0]; if (f && f.size > 10*1024*1024) { toast.error('Max 10MB'); return; } setComposeAttachment(f || null); }} />
                </label>
                {composeAttachment && (
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border text-sm">
                    <span className="truncate max-w-[180px] text-gray-700">{composeAttachment.name}</span>
                    <button type="button" onClick={() => { setComposeAttachment(null); if (composeFileRef.current) composeFileRef.current.value = ''; }}
                      className="text-red-400 hover:text-red-600 p-0.5">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="px-6 pb-6">
            <button type="submit" disabled={sending || !selectedRecipient}
              className="bg-[#0B5D1E] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#062E10] transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm">
              {sending ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>}
              Send Message
            </button>
          </div>
        </form>
      )}

      {/* Filters */}
      <div className="bg-[#F8FAFB] rounded-xl p-4 border border-gray-100 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search messages..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none text-sm" />
          </div>
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
            className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 min-w-[180px]">
            <option value="">All Sources</option>
            <option value="contact">Contact Form</option>
            <option value="surveyor_inquiry">Surveyor Inquiry</option>
            <option value="member">Member Message</option>
            <option value="admin">Admin Sent</option>
          </select>
          <select value={readFilter} onChange={(e) => setReadFilter(e.target.value)}
            className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 min-w-[140px]">
            <option value="">All Status</option>
            <option value="false">Unread</option>
            <option value="true">Read</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#0B5D1E]/20 border-t-[#0B5D1E] rounded-full animate-spin" />
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-20">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-bold text-gray-900 mb-1">No Messages</h3>
          <p className="text-gray-500 text-sm">No messages match your current filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Message List */}
          <div className="lg:col-span-1 space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {messages.map((msg) => (
              <button key={msg.id} onClick={() => { setSelectedMsg(msg); setReplyText(''); if (!msg.is_read) markRead(msg); }}
                className={`w-full text-left p-4 rounded-xl border transition-all ${
                  selectedMsg?.id === msg.id
                    ? 'border-[#0B5D1E] bg-[#0B5D1E]/5 shadow-sm'
                    : msg.is_read
                      ? 'border-gray-100 bg-white hover:bg-gray-50'
                      : 'border-[#C8A951]/30 bg-[#FFFDF5] hover:bg-[#FFF9E6]'
                }`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {!msg.is_read && <span className="w-2 h-2 rounded-full bg-[#0B5D1E] flex-shrink-0" />}
                      <span className="font-semibold text-sm text-[#111827] truncate">{msg.source === 'admin' ? (msg.recipient_name || 'Member') : (msg.sender_name || msg.name)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{msg.subject}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${badge(msg.source).cls}`}>
                      {badge(msg.source).label}
                    </span>
                    {msg.attachment && (
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">{new Date(msg.created_at).toLocaleString()}</p>
              </button>
            ))}
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-2">
            {selectedMsg ? (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Detail Header */}
                <div className="bg-[#F8FAFB] px-6 py-4 border-b border-gray-100 flex items-start justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-[#111827]">{selectedMsg.subject}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedMsg.source === 'admin' ? (
                        <>To <strong>{selectedMsg.recipient_name || 'Member'}</strong></>
                      ) : (
                        <>From <strong>{selectedMsg.sender_name || selectedMsg.name}</strong> &lt;{selectedMsg.email}&gt;</>
                      )}
                    </p>
                    {selectedMsg.source === 'surveyor_inquiry' && selectedMsg.surveyor_name && (
                      <p className="text-xs text-purple-600 mt-1 font-medium">
                        Inquiry about: {selectedMsg.surveyor_name}
                        {selectedMsg.surveyor_membership_number && ` (${selectedMsg.surveyor_membership_number})`}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${badge(selectedMsg.source).cls}`}>
                      {badge(selectedMsg.source).label}
                    </span>
                    {canDelete(PERMISSIONS.DELETE_MESSAGES) && (
                      <button onClick={() => deleteMsg(selectedMsg)} className="text-red-400 hover:text-red-600 transition p-1" title="Delete">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Message Body */}
                <div className="p-6">
                  <p className="text-xs text-gray-400 mb-4">{new Date(selectedMsg.created_at).toLocaleString()}</p>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedMsg.message}</div>

                  {/* Attachment */}
                  {selectedMsg.attachment && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-500 mb-2">Attachment</p>
                      <a href={selectedMsg.attachment} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 border rounded-lg hover:bg-gray-100 transition text-sm text-[#0B5D1E] font-medium">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        View Attachment
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  )}

                  {/* Existing Reply */}
                  {selectedMsg.admin_reply && (
                    <div className="mt-6 pt-6 border-t border-gray-100">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full bg-[#C8A951] flex items-center justify-center text-[#062E10] text-[10px] font-bold">A</div>
                        <p className="text-sm font-semibold text-gray-900">Your Reply</p>
                        <span className="text-[10px] text-gray-400">
                          {selectedMsg.replied_at && new Date(selectedMsg.replied_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-[#FFFDF5] rounded-lg p-4 border border-[#C8A951]/20 ml-8">
                        {selectedMsg.admin_reply}
                      </div>
                    </div>
                  )}

                  {/* Reply Box */}
                  {selectedMsg.source === 'member' && (
                    <div className="mt-6 pt-6 border-t border-gray-100">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {selectedMsg.admin_reply ? 'Update Reply' : 'Reply to Member'}
                      </label>
                      <textarea
                        value={replyText} onChange={(e) => setReplyText(e.target.value)}
                        rows={3} placeholder="Type your reply..."
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none resize-none text-sm"
                      />
                      <button onClick={handleReply} disabled={replying || !replyText.trim()}
                        className="mt-2 bg-[#0B5D1E] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                        {replying ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                        )}
                        Send Reply
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="text-sm">Select a message to view</p>
              </div>
            )}
          </div>
        </div>
      )}
      </>
      )}
        </div>

        {/* Online Members Panel - Right Sidebar (desktop) */}
        <div className="hidden lg:block w-72 flex-shrink-0">
          <div className="sticky top-24">
            <OnlineMembersPanel
              currentUserRole="admin"
              activeChatUserId={chatUser?.id}
              onSelectMember={(member) => {
                setChatUser(member);
                setShowCompose(false);
                setSelectedMsg(null);
              }}
            />
          </div>
        </div>

        {/* Online Members Panel - Mobile/Tablet toggle button */}
        <button
          onClick={() => setShowOnlinePanel(!showOnlinePanel)}
          className="lg:hidden fixed bottom-6 right-6 z-40 bg-[#0B5D1E] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#062E10] transition-all"
          title="Online Members"
        >
          <div className="relative">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-green-400 rounded-full border-2 border-[#0B5D1E] animate-pulse" />
          </div>
        </button>

        {/* Online Members Panel - Mobile/Tablet slide-over */}
        {showOnlinePanel && (
          <>
            <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setShowOnlinePanel(false)} />
            <div className="lg:hidden fixed right-0 top-0 h-full w-80 max-w-[85vw] bg-[#F8FAFB] z-50 shadow-2xl overflow-y-auto">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
                <h3 className="text-sm font-bold text-gray-900">Online Members</h3>
                <button onClick={() => setShowOnlinePanel(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-3">
                <OnlineMembersPanel
                  currentUserRole="admin"
                  activeChatUserId={chatUser?.id}
                  onSelectMember={(member) => {
                    setShowOnlinePanel(false);
                    setChatUser(member);
                    setShowCompose(false);
                    setSelectedMsg(null);
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
